/**
 * @file EditClinicView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import { Avatar, Button, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Box } from "@mui/material";
import { Container } from "@mui/system";
import { ThemeProvider } from "@mui/material/styles";
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { NewAdminTitle, NewClinicButton} from "../themes/AddNewAdminDialogTheme";
import "../../../common/css/Common.css"
import "./NewClinic.css";
import { BaseComponent } from "../../base/BaseComponent";
import { Clinic } from "../../../../services/models/Clinic";
import { TextFieldWrapper } from "../../../common/customviews/TextFieldWrapper";
import { GenericResponse } from "../../../../services/models/GenericResponse";
import { ApiError } from "../../../../services/models/ApiError";
import { AppAlert } from "../../../utils/AppAlert";
import { ClinicViewModel } from "../viewmodels/ClinicViewModel";
import { roboto500Text16 } from "../../../utils/theme/CommonTheme";

/**
 * A type of Component state
 */
type CompState = {
    errorToggle: boolean, backDropLoading: boolean, submitted: boolean, errorMessage: string,
    clinicname: string, enabled: boolean
};

/**
 * An interface definition for Component Props
 */
interface Props {
    onDialogDismissed: any,
    onClinicEdited: any,
    clinic?: Clinic
}

/**
 * A class definition for component EditClinicView
 */
export class EditClinicView extends BaseComponent<Props, CompState> {

    viewModel: ClinicViewModel;
    initialName: string;
    constructor(props: Props) {
        super(props)
        this.viewModel = new ClinicViewModel;
        const clinicName = this.props.clinic?.name!!; // eslint-disable-line
        this.initialName = clinicName;
        this.state = {
            errorToggle: false, backDropLoading: false,
            submitted: false, clinicname : clinicName,  errorMessage: "", enabled: false
        };
    }

    /**
     * Further delegates dismiss event
     */
    private dismissDialog = () => {
        this.props.onDialogDismissed();
    }
    
    
    /**
     * Hides loading
     */
    private hideLoading = (message: string)=> {
        this.setState({
            errorMessage: message,
            errorToggle: true,
            backDropLoading: false
        });
    }

    
    /**
     * Shows loading
     */
    private showLoading = ()=>{
        this.setState({
            errorToggle: false,
            backDropLoading: true
        })
    }

    /**
     * Subscribes events
     */
    private subscribeApiEvenets = () =>{   
        this.viewModel.onClinicEdited.subscribe((response: GenericResponse)=>{
            this.setState({
                errorToggle: false,
                backDropLoading: false,
            });
            this.logger.verbose(`Clinic edited : ${JSON.stringify(response)}`, EditClinicView.name);
            this.props.onClinicEdited();
        });

        this.viewModel.onClinicEditedError.subscribe((apiError: ApiError)=> {
            this.logger.error(`Error while Clinic editing : ${JSON.stringify(apiError)}`, EditClinicView.name);
            this.hideLoading(`${apiError.message}`);
        });
    }

    /**
     * Activates admins
     * @returns 
     */
    private editClinic = async () => {
            const accessToken = this.validateAndGetSession(EditClinicView.name);
            this.viewModel.editClinic(this.state.clinicname, this.props.clinic!!.id!!, accessToken); // eslint-disable-line
            this.showLoading();
    }

    componentDidMount(): void {
        this.subscribeApiEvenets();
    }

    componentWillUnmount(): void {
        try {
            this.viewModel.onClinicCreated.unsubscribe();
            this.viewModel.onClinicCreatedError.unsubscribe();
        } catch(e: unknown) {
            this.logger.warning(JSON.stringify(e), EditClinicView.name);
        }
    }
    
    /**
     * Handles backdrop closing
     */
    private handleBackDropClose = () => {
        this.setState({
            backDropLoading: false
        })
    };

    /**
     * Calls on change of clinicname values
     * @param email string
     */    
    private onChangeClinicName = (clinicname: string) => {
        const trimClinicName = clinicname.trim();
        const enabled = trimClinicName.length > 0 && this.initialName.toUpperCase() != trimClinicName.toUpperCase();
        this.setState({ clinicname: trimClinicName, enabled})
    }

    render() {
        return <>
            <div>
                <Box
                    sx={{
                        display: "flex",
                        "& > :not(style)": {
                            m: 0,
                            width: "100%",
                            minHeight: "200px"
                        },
                    }}>
                    <Paper elevation={0} className="UpdateClinicBox">
                        <Grid container spacing={0}>
                            <Grid item xs={9}>
                                <ThemeProvider theme={NewAdminTitle}>
                                    <Typography className="TitleUniqueId">
                                        Update a clinic name
                                    </Typography>
                                </ThemeProvider >
                            </Grid>
                            <Grid item xs={3} container justifyContent="flex-end">
                            <IconButton className="CloseButton" onClick={this.dismissDialog}><Avatar src={"/../closeIcon.svg"} /></IconButton>                            
                        </Grid>
                        </Grid>

                        <Container disableGutters maxWidth={false}>
            
                                <Container disableGutters maxWidth={false} className="DefaultMargin">
                                        <Stack
                                                        direction="row"
                                                        justifyContent="flex-end"
                                                        alignItems="center"
                                                        spacing={2} sx={{ marginRight: "4vh !important"}}>
                                          <ThemeProvider theme={roboto500Text16}>
                                            <TextFieldWrapper size="medium" fullWidth id="outlined-basic" label="Clinic name" variant="outlined"
                                                className="NewClinicTextField" inputProps={{ maxLength: 40, style: { textTransform: 'capitalize'} }} onChange={(e) => { this.onChangeClinicName(e.target.value) }}
                                                defaultValue={this.state.clinicname} autoFocus />
                                          </ThemeProvider>
                                          </Stack>
                                </Container>
                                 <Stack
                                            direction="row"
                                            justifyContent="flex-end"
                                            alignItems="flex-end" className="EdiClinicDefaultMargin"
                                            spacing={2}>
                                            <ThemeProvider theme={NewClinicButton}>
                                                <Button variant="contained" size="large" id="CancelUpdate"
                                                    disableElevation className="BackNewDialogBtn"  onClick={this.dismissDialog}>
                                                    Cancel
                                                </Button>
                                            </ThemeProvider>
                                            <ThemeProvider theme={NewClinicButton}>
                                                <Button variant="contained" id="UpdateClinic" onClick={this.editClinic} size="large"
                                                    disableElevation className="BackNewDialogBtn" disabled={!this.state.enabled}>
                                                    &nbsp;&nbsp;Save&nbsp;&nbsp;
                                                </Button>
                                            </ThemeProvider>
                                        </Stack>
                            </Container>        
                            
                    </Paper>
                </Box>
                <BackDropLoadingView handleBackDropClose={this.handleBackDropClose} isLoading={this.state.backDropLoading} />
                {
                    this.state.errorToggle && <AppAlert severity="error">
                        {this.state.errorMessage}
                    </AppAlert>
                }
            </div>
        </>
    }

}